// Reglas de producto compartidas. Un dato, un helper: la pantalla de gestión, la
// tarjeta y la fila de la tabla tienen que responder LO MISMO a "¿se puede retirar
// esto?" — tres comparaciones inline serían tres verdades y el botón acabaría
// habilitado en una vista y no en otra.

/**
 * ¿Se puede retirar esta referencia? Solo con el stock a CERO (decisión del club,
 * 15-08-2026).
 *
 * Con género dentro, esconder el producto lo deja invisible para el POS mientras
 * sigue contando en el inventario: descuadre garantizado en el primer recuento.
 * Primero se gasta o se ajusta a cero, luego se retira.
 *
 * Stock NEGATIVO sí deja retirar: no hay género que esconder, y son justo las
 * referencias basura que interesa poder apartar (el producto "Inscripción" de Kong
 * Club arrastra −665 del modelo viejo).
 *
 * Espejo del guard del backend, que devuelve 409 — aquí solo evitamos el viaje.
 */
export function canRetireProduct(product) {
  return Number(product?.stock_quantity ?? 0) <= 0;
}

/**
 * Motivo por el que retirar no es directo, o null si sí lo es. Para el tooltip.
 *
 * La regla del 15-ago no cambia —solo se retira con las existencias a cero—, pero
 * desde el atajo de «poner a cero y retirar» el botón ya NO está muerto: al pulsarlo
 * se ofrece hacer las dos cosas de un tirón. El texto lo dice, porque un tooltip que
 * describe un callejón sin salida delante de un botón que sí hace algo es peor que
 * no tener tooltip.
 */
export function retireBlockedReason(product) {
  if (canRetireProduct(product)) return null;
  return `Quedan ${product?.stock_quantity} en stock: se te ofrecerá ponerlas a cero y retirar.`;
}

/**
 * ¿Este producto se vende por PESO? En `unit_type: "weight"` el precio es €/gramo
 * y las cantidades (carrito, comanda, stock) son gramos, no unidades.
 *
 * Vive aquí y no en cada pantalla porque el mismo dato lo tienen que leer igual la
 * carta, la barra y el carrito: si una dice unidades donde otra dice gramos, la
 * comanda pide "250×" un queso que se vendió a 250 g (reporte CRUO, 19-ago-2026).
 *
 * Acepta tanto un producto del catálogo como una línea de carrito/comanda: las dos
 * traen el dato en `unit_type`.
 */
export function esPeso(item) {
  return item?.unit_type === "weight";
}

/**
 * Cantidad de una línea, legible. El backend manda `Decimal` con escala 3, así que 4 unidades
 * llegan como "4.000" y la barra leía «4.000× CERVEZA CRUA» — que parecen cuatro mil cañas
 * (reporte CRUO, 24-ago-2026). `Number` se come los ceros de cola sin tocar los decimales que
 * sí importan: 4.000 → 4, y 3.500 g → 3.5 g.
 *
 * Vive aquí, con `esPeso`, porque el mismo número lo pintan la carta, la barra y el carrito.
 */
export function cantidadLegible(quantity) {
  const n = Number(quantity);
  return Number.isFinite(n) ? n : quantity;
}

/** Sufijo de unidad para pintar precios y cantidades: "g" por peso, "u" por unidad. */
export function unidadDe(item) {
  return esPeso(item) ? "g" : "u";
}

/**
 * Nombres que el backend usa como categoría-cajón de «sin categoría»: el alta de
 * producto asigna una categoría REAL llamada «Uncategorized» en vez de dejar
 * category_id a NULL, así que "sin categoría" vive en dos formas a la vez.
 */
export const UNCATEGORIZED_NAMES = ['Uncategorized', 'uncategorized', 'Sin categoría'];

export function esSinCategoria(product) {
  const n = product?.category?.name;
  return !n || UNCATEGORIZED_NAMES.includes(n);
}

/**
 * Filtro de la píldora de categoría del Almacén. Grid y tabla tienen que responder
 * LO MISMO: la selección masiva («seleccionar todos» + borrado) opera sobre los
 * productos visibles, así que dos copias divergentes de este filtro significan que
 * un borrado masivo alcanza conjuntos distintos según la vista (reporte Kong,
 * 20-ago-2026: el buscador pisaba la píldora y se enseñaba otra categoría).
 * La píldora se aplica SIEMPRE, también con búsqueda activa: píldora ∩ búsqueda.
 */
export function filtrarPorPildora(products, selectedCategory) {
  const arr = Array.isArray(products) ? products : [];
  if (!selectedCategory || selectedCategory === 'All') return activosPrimero(arr);
  if (selectedCategory === 'Sin categoría') return activosPrimero(arr.filter(esSinCategoria));
  return activosPrimero(arr.filter((p) => p.category?.name === selectedCategory));
}

/**
 * ¿Está retirado? Un solo sitio para la comparación: tarjeta, fila, badge y orden
 * tienen que coincidir (`is_active` ausente cuenta como activo, igual que el badge).
 */
export function esRetirado(product) {
  return product?.is_active === false;
}

/**
 * Motivo por el que NO se mueve stock de un producto, o null si sí se puede.
 * Un retirado no se ve en el POS pero su stock sí cuenta en el inventario: entrar
 * género ahí es el descuadre que la regla del 15-ago evita al retirar. Se reactiva primero.
 * Un solo texto/regla para rejilla y tabla.
 */
export function stockBloqueadoMotivo(product) {
  return esRetirado(product) ? 'Está retirado: reactívalo para mover su stock.' : null;
}

/**
 * Activos primero, retirados al final, SIN alterar el orden interno de cada grupo.
 * Los retirados no se pueden borrar (cuentas y ledger de stock) y se acumulan: al
 * mezclarse con los vivos nadie los distingue a primera vista. Se aplica dentro de
 * `filtrarPorPildora`, así rejilla, tabla y selección masiva ven el MISMO orden.
 * Devuelve el mismo array si no hay retirados (sin copia, sin re-render de más).
 */
export function activosPrimero(products) {
  const arr = Array.isArray(products) ? products : [];
  if (!arr.some(esRetirado)) return arr;
  return [...arr.filter((p) => !esRetirado(p)), ...arr.filter(esRetirado)];
}

/** Estado vacío del Almacén: decir QUÉ filtro dejó la lista a cero, incluidos los dos a la vez. */
export function mensajeSinProductos(searchQuery, selectedCategory) {
  const conBusqueda = (searchQuery || '').trim().length > 0;
  const conPildora = selectedCategory && selectedCategory !== 'All';
  if (conBusqueda && conPildora) {
    const donde = selectedCategory === 'Sin categoría' ? 'sin categoría' : `en la categoría "${selectedCategory}"`;
    return `No se encontraron productos para esa búsqueda ${donde}`;
  }
  if (conBusqueda) return 'No se encontraron productos para esa búsqueda';
  if (selectedCategory === 'Sin categoría') return 'No se encontraron productos sin categoría asignada';
  if (conPildora) return `No se encontraron productos en la categoría "${selectedCategory}"`;
  return 'No hay productos disponibles';
}

/**
 * Tolerancia de báscula en dispensación a peso: ±0,10 g ABSOLUTOS (regla firmada
 * del club — no porcentaje). La misma constante vive en el backend
 * (constants.MERMA_TOLERANCIA_G) que valida en servidor; aquí es el aviso
 * temprano para no hacer viajar un peso que va a ser rechazado.
 */
export const MERMA_TOLERANCIA_G = 0.10;

/** Merma de una línea: peso real de báscula − gramos cobrados (3 decimales). */
export function mermaDe(cobrado, bascula) {
  return Number(((Number(bascula) || 0) - (Number(cobrado) || 0)).toFixed(3));
}

/**
 * La cantidad que se ENSEÑA de una línea de venta: lo DESPACHADO.
 *
 * Si hubo báscula (`real_weight`), ese es el gramo que salió — y es el que ve el
 * socio en su historial, el camarero en la barra y la gestoría en el CSV. Sin
 * báscula (o producto por unidad), lo pedido (`quantity`). El COBRO no cambia: va
 * por lo pedido; la diferencia es la merma (`mermaDeLinea`). Decidido con Kong el
 * 05-09-2026 tras un mes de «las pantallas dicen 1,456 y salieron 1,504»: había
 * 13 renders decidiendo cada uno, 12 con el teórico y el real de nota al pie.
 * Regla un-dato-un-render: ningún render elige por su cuenta entre las dos.
 *
 * No exige `unit_type`: `real_weight` solo existe en líneas a peso por
 * construcción (make_purchase lo ignora en productos por unidad).
 */
/**
 * ¿Esta línea trae una lectura de báscula utilizable?
 *
 * El criterio es `> 0`, no `!= null`: un peso de 0 no es «pesado a cero», es una
 * línea sin báscula —lo mismo que decidió el backend en `utils/despachado.py`—.
 * Existe porque había pantallas decidiéndolo con `!= null` por su cuenta, y una
 * línea con báscula 0 salía pesada en unas y sin pesar en otras (misma clase de
 * bug que las seis copias que el backend acaba de unificar, BE #601).
 */
// spec: stock-almacen#merma
// «Sin báscula, lo despachado es lo pedido y no hay merma.»
export function hayBascula(item) {
  const real = item?.real_weight ?? item?.realWeight;
  return real != null && Number(real) > 0;
}

// spec: stock-almacen#merma
// «Lo que se ENSEÑA como despachado es el peso real» — el cobro va por lo pedido.
// Gemelo BE: utils/despachado.py → cantidad_despachada.
export function cantidadDespachada(item) {
  if (hayBascula(item)) return Number(item?.real_weight ?? item?.realWeight);
  return Number(item?.quantity) || 0;
}

// spec: stock-almacen#merma
/** Merma de la línea (real − pedido) o null si no hubo báscula. */
export function mermaDeLinea(item) {
  if (!hayBascula(item)) return null;
  return mermaDe(item?.quantity, item?.real_weight ?? item?.realWeight);
}

/**
 * La nota que acompaña al despachado cuando difiere de lo pedido:
 * «pedido 1.456 g · merma +0.048 g». Cadena vacía si no hay báscula o coinciden.
 */
export function notaPedidoMerma(item) {
  const merma = mermaDeLinea(item);
  if (merma == null || merma === 0) return "";
  const signo = merma > 0 ? "+" : "";
  return `pedido ${cantidadLegible(item?.quantity)} g · merma ${signo}${merma} g`;
}

/**
 * ¿Esta línea de VENTA va a peso? `unit_type` manda; solo falta cuando el
 * producto se borró, y entonces una cantidad con decimales delata el gramo.
 *
 * Una sola respuesta para la etiqueta y para la anulación: el historial lo
 * decidía dos veces —con y sin ese respaldo— y una línea huérfana con 1,5 se
 * pintaba con papelera de línea entera pero anulaba UNA unidad.
 */
export function lineaAPeso(item) {
  const ut = item?.unit_type ?? item?.unitType;
  if (ut) return esPeso({ unit_type: ut });
  // Sin unit_type: una lectura de báscula solo existe en líneas a peso (make_purchase
  // la descarta en las de unidades), y unos decimales tampoco son unidades.
  if (hayBascula(item)) return true;
  const q = Number(item?.quantity);
  return Number.isFinite(q) && q % 1 !== 0;
}

/**
 * La cantidad de una línea de venta, lista para pintar: «2.05 g» o «2 u».
 *
 * «Peso» delante solo decía algo en las líneas a granel; en las de unidades
 * salía «Fanta Limón Peso 2u» (captura de Yago, 12-10-2026). El número es lo
 * despachado (`cantidadDespachada`); lo pedido y la merma, de nota aparte.
 */
export function etiquetaCantidadLinea(item) {
  const n = cantidadLegible(Number(cantidadDespachada(item)).toFixed(3));
  return `${n} ${lineaAPeso(item) ? "g" : "u"}`;
}
